import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  postsSetEditor,
  postsSendEmail,
  postsPostDelete,
  postsGetAllPosts,
  postsSetClientsFilter,
  postsSetFilter,
  postsGetHistory,
  postsClearHistory,
  postsUpdateNewPosts,
  postsSaveDraft,
  postsSetCurrentDraft,
  postsGetDrafts,
  postsDeleteDraft,
  postsHistorySetSearchQuery,
  postsDeleteKeyword,
  postsAddWsPost,
  postsMarkAsViewed, postsMarkAsRead,
} from "./actions";
import { IEmail, ISinglePost, IDeletePost, FilterClient, FilterPosts, node, IHistoryEmail, IMultiPost } from "./types";
import { createChannel } from "../../api";
import { personToPositionTitle } from "../../utilites/personToPositionTitle";
import { commiteeSessionValidator } from "../../utilites/commiteeSessionValidator";

export const usePostsActions = () => {
  const dispatch = useDispatch();

  const onGetPosts = useCallback(() => {
    dispatch(postsGetAllPosts())
  }, [dispatch]);

  // Add new posts from WebSocket
  // Returns a cleanup function that stops reconnecting and closes the socket
  const onWatchForPosts = useCallback((token: string) => {
    let stopped = false
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let channel: WebSocket | null = null

    const connect = () => {
      const ws = createChannel(token);
      channel = ws
      ws.addEventListener('open', () => {
        console.log('ws-connected')
      })
      //Reconnect when connection is closed (unless cleanup was requested)
      ws.addEventListener('close', () => {
        if (stopped) return
        console.log('ws disconnected')
        reconnectTimer = setTimeout(connect, 3000)
      })
      //Close the socket on error to trigger the reconnect above
      ws.addEventListener('error', () => {
        ws.close()
      })

      //Message listener
      ws.addEventListener('message', (e: any) => {
        if (e.data) {
          let {data, sender} = JSON.parse(e.data)
          if (data) {
            //Custom titles for "press_release"
            if(sender === "press_release") {
              data = {...data, title: `חדשות הכנסת- ${data.title}`}
            }
            if(sender === "person_to_position") {
              data = personToPositionTitle(data)
            }
            if(sender === "committee_session" && !commiteeSessionValidator(data)) {
              return
            }
            // "postsAddWsPost" - appends into posts immediately
            dispatch(postsAddWsPost({...data, _sender: sender}))
          }
        }
      })
    }

    connect()

    return () => {
      stopped = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      channel?.close()
    }
  }, [dispatch]);

  const onUpdateNewPosts = () => {
    dispatch(postsUpdateNewPosts())
  }

  const onSetEditor = useCallback((post: ISinglePost | IMultiPost | null) => {
    dispatch(postsSetEditor(post));
  }, [dispatch]);

  const onSetClientsFilter = useCallback((client: FilterClient | null) => {
    dispatch(postsSetClientsFilter(client))
  }, [dispatch])

  const onSendEmail = (email: IEmail) => {
    dispatch(postsSendEmail(email));
  };

  const onSaveDraft = (draft: IEmail) => {
    dispatch(postsSaveDraft(draft))
  }

  const onDeletePost = (payload: IDeletePost) => {
    dispatch(postsPostDelete(payload));
  };


  const onApplyFilter = (params: FilterPosts) => {
    dispatch(postsSetFilter(params))
  }

  const onGetHistory = () => {
    dispatch(postsGetHistory())
  }

  const onSetSearchQuery = (query: string) => {
    dispatch(postsHistorySetSearchQuery(query))
  }

  const onClearHistory= () => {
    dispatch(postsClearHistory())
  }

  const onMarkAsViewed = (sender: node, id: number) => {
    dispatch(postsMarkAsViewed({sender, id}))
  }

  const onMarkAsRead = (sender: node, id: number) => {
    dispatch(postsMarkAsRead({sender, id}))
  }

  const onSetCurrentDraft = (draft: IHistoryEmail | null) => {
    dispatch(postsSetCurrentDraft(draft))
  }

  const onGetDrafts = () => {
    dispatch(postsGetDrafts())
  }

  const onDeleteDraft = (draftId: number) => {
    dispatch(postsDeleteDraft(draftId))
  }

  const onDeletePostKeyword = useCallback((sender: node, postId: number, keywordId: number) => {
    dispatch(postsDeleteKeyword({sender, postId, keywordId}))
  }, [dispatch])



  return {
    onMarkAsRead,
    onGetPosts,
    onUpdateNewPosts,
    onSetClientsFilter,
    onDeletePost,
    onWatchForPosts,
    onSetEditor,
    onSendEmail,
    onApplyFilter,
    onGetHistory,
    onSetSearchQuery,
    onClearHistory,
    onMarkAsViewed,
    onSaveDraft,
    onSetCurrentDraft,
    onGetDrafts,
    onDeleteDraft,
    onDeletePostKeyword
  };
};
